import { getI18n } from 'react-i18next';
import { Action } from 'redux-actions';
import { call, fork, put, take } from 'redux-saga/effects';
import Command from '../action/command';
import Event from '../action/event';
import {
    onEnqueueErrorSnackbar,
    onEnqueueSuccessSnackbar,
} from './enqueueSnackbarFlow';
import { CreateIssueReportPayload } from '../action/issueReportsCommands';
import { Api } from '../api';
import Logger from '../common/logger';
import { ProfileInfo } from '../api/types';

function* onCreateIssueReport(
    message: string,
    assets: {
        assetId: string;
        depotId: string;
        door: number;
    }[],
    issueType: string,
    service: string,
    bookingNumber: string,
    orderId?: string,
    profileInfo?: ProfileInfo,
    locationId?: string,
) {
    try {
        const created: { id: string } | null = yield call(
            Api.createIssueReports,
            message,
            assets,
            issueType,
            service,
            bookingNumber,
            orderId,
            profileInfo,
            locationId,
        );
        yield fork(
            onEnqueueSuccessSnackbar as any,
            getI18n().t('snackbar.success.create_issue_report'),
            12000,
        );
        yield put(
            Event.IssueReport.createdIssueReport({
                success: true,
                // Lets the caller that sent the user here (e.g. the goodwill
                // code form) link the new report without re-searching for it.
                issueReportId: created?.id,
            }),
        );
    } catch (error) {
        Logger('Saga').error(error);
        yield fork(
            onEnqueueErrorSnackbar as any,
            getI18n().t('snackbar.error.create_issue_report'),
        );
        // `failed`, never `success: true`. This branch used to claim success so
        // the form would stop loading, which also made it navigate away — so a
        // failed create was indistinguishable from a saved one, and the report
        // was simply missing from the list afterwards. The form now stays put
        // with the agent's input intact.
        yield put(
            Event.IssueReport.createdIssueReport({
                success: false,
                failed: true,
            }),
        );
    }
}

export function* createIssueReportFlow() {
    while (true) {
        const action: Action<CreateIssueReportPayload> = yield take(
            Command.IssueReport.createIssueReport.toString(),
        );

        yield fork(
            onCreateIssueReport as any,
            action.payload.message,
            action.payload.assets,
            action.payload.issueType,
            action.payload.service,
            action.payload.bookingNumber,
            action.payload.orderId,
            action.payload.profileInfo,
            action.payload.locationId,
        );
    }
}
